import { useEffect, useRef, useState } from 'react';

/**
 * useWakeWord — best-effort "Hey Moe" voice activation via the browser's
 * Web Speech API (no cost, no backend). Off by default (requires user
 * opt-in via the Moe Settings toggle, persisted in localStorage).
 *
 * Returns { supported, enabled, setEnabled, listening, transcript }.
 * When the phrase "hey moe" is detected, onTrigger is called.
 *
 * Browser support: Chrome/Edge (good), Safari iOS 14.5+ (partial), Firefox (no).
 */
export function useWakeWord(onTrigger) {
  const [supported, setSupported] = useState(false);
  const [enabled, setEnabledState] = useState(false);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);
  const triggerRef = useRef(onTrigger);
  triggerRef.current = onTrigger;

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    setSupported(!!SR);
    const saved = localStorage.getItem('moe.wakeWord.enabled') === '1';
    setEnabledState(saved);
  }, []);

  useEffect(() => {
    if (!supported || !enabled) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onstart = () => setListening(true);
    rec.onerror = () => setListening(false);
    rec.onend = () => {
      setListening(false);
      // Auto-restart unless user turned it off
      if (enabled && recognitionRef.current === rec) {
        try { rec.start(); } catch {}
      }
    };
    rec.onresult = (event) => {
      let latest = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        latest += event.results[i][0].transcript;
      }
      const norm = latest.toLowerCase().trim();
      setTranscript(norm.slice(-60));
      // Match "hey moe" with light tolerance for "hey mo" / "hey moh"
      if (/\bhey\s+(moe|mo|moh|mow)\b/.test(norm)) {
        triggerRef.current?.();
        // Reset the recognizer so repeated phrases each trigger
        try { rec.stop(); } catch {}
      }
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch {}

    return () => {
      recognitionRef.current = null;
      try { rec.stop(); } catch {}
      setListening(false);
    };
  }, [supported, enabled]);

  const setEnabled = (next) => {
    localStorage.setItem('moe.wakeWord.enabled', next ? '1' : '0');
    setEnabledState(next);
  };

  return { supported, enabled, setEnabled, listening, transcript };
}
